// frontend/src/features/wind/CompSetPanel.js
import React, { useMemo } from "react";
import { SectionHeader, PlaceholderRow } from "./ui";

/**
 * Right-hand panel: subject view OR comp set editor,
 * with AV/MW and over/under stats.
 */
export default function CompSetPanel({
  compMode,
  selectedProject,
  selectedProjects,
  subjectId,
  onSubjectIdChange,
  compValues,
  onUpdateAssessedValue,
}) {
  const compRows = useMemo(() => {
    return (selectedProjects || []).map((p) => {
      const form = compValues[p.id] || {};
      const capacity =
        typeof p.capacity_mw === "number" ? Number(p.capacity_mw) : null;
      const assessedNum = parseFloat((form.assessed || "").replace(/,/g, ""));
      const assessed =
        Number.isFinite(assessedNum) && assessedNum >= 0 ? assessedNum : null;
      const avPerMw =
        capacity && capacity > 0 && assessed != null
          ? assessed / capacity
          : null;

      return {
        projectId: p.id,
        name: p.name || "Unnamed project",
        state: p.state || null,
        counties: Array.isArray(p.counties) ? p.counties : [],
        capacity,
        turbines: p.turbine_count || null,
        year: p.year || null,
        lat: p.lat,
        lon: p.lon,
        assessed,
        avPerMw,
      };
    });
  }, [selectedProjects, compValues]);

  const subjectRow = useMemo(
    () => compRows.find((r) => r.projectId === subjectId) || null,
    [compRows, subjectId]
  );

  const peerRows = useMemo(
    () => compRows.filter((r) => r.projectId !== subjectId),
    [compRows, subjectId]
  );

  const compStats = useMemo(() => {
    const peersWithAV = peerRows.filter(
      (r) => typeof r.avPerMw === "number" && r.avPerMw > 0
    );
    if (!subjectRow || !peersWithAV.length) {
      return null;
    }
    const sorted = peersWithAV
      .map((r) => r.avPerMw)
      .sort((a, b) => a - b);
    const mid = Math.floor(sorted.length / 2);
    const median =
      sorted.length % 2 === 0
        ? (sorted[mid - 1] + sorted[mid]) / 2
        : sorted[mid];

    const subj = subjectRow.avPerMw;
    if (!median || !subj) return null;

    const delta = subj - median;
    const overPct = median > 0 ? delta / median : 0;

    return {
      medianAvPerMw: median,
      subjectAvPerMw: subj,
      delta,
      overPct,
      peerCount: peersWithAV.length,
    };
  }, [peerRows, subjectRow]);

  return (
    <div className="rounded-2xl border border-slate-800 bg-slate-900/70 px-4 py-4 lg:px-5 lg:py-5 flex flex-col h-full">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <h2 className="text-sm font-semibold tracking-tight text-slate-50">
            {compMode ? "Comp Set editor" : "Assessment workspace"}
          </h2>
          <p className="text-[11px] text-slate-400">
            {compMode
              ? "Review your selected wind farms, pick a subject, and enter assessed values to see AV/MW and over/under signals."
              : "Select a wind farm on the left or click a marker on the map to see its profile. When you’re ready, select multiple farms and click Start Comp Set."}
          </p>
        </div>
        <span className="inline-flex items-center rounded-full border border-indigo-400/50 bg-indigo-900/30 px-2 py-0.5 text-[10px] text-indigo-100">
          {compMode ? "Comp Set" : "Subject view"}
        </span>
      </div>

      <div className="mt-1 space-y-3 text-[11px] text-slate-300">
        {compMode && compRows.length ? (
          <>
            <SectionHeader label="Comp Set (in memory)" />
            <CompSetTable
              rows={compRows}
              subjectId={subjectId}
              onSubjectIdChange={onSubjectIdChange}
              compValues={compValues}
              onUpdateAssessedValue={onUpdateAssessedValue}
            />
            <SectionHeader label="AV/MW snapshot" />
            <CompStatsPanel stats={compStats} />
            <SectionHeader label="Next steps" />
            <ul className="list-disc pl-5 space-y-1 text-[11px] text-slate-400">
              <li>
                Once this flow feels right, we’ll persist these values as{" "}
                <span className="font-semibold">wind_assessments</span>.
              </li>
              <li>
                Then we’ll introduce named Comp Sets that survive refresh and
                feed directly into packet-building tools.
              </li>
              <li>
                For now, treat this as an interactive scratch pad: type values,
                see AV/MW and peer signals, and export your grid manually if
                needed.
              </li>
            </ul>
          </>
        ) : (
          <>
            <SectionHeader label="Project profile (from USWTDB)" />
            {selectedProject ? (
              <>
                <PlaceholderRow
                  label="Project name"
                  value={selectedProject.name || "Unnamed project"}
                />
                <PlaceholderRow
                  label="State / Counties"
                  value={
                    [
                      selectedProject.state || "State TBD",
                      (selectedProject.counties || []).length
                        ? `${selectedProject.counties.join(", ")}`
                        : null,
                    ]
                      .filter(Boolean)
                      .join(" · ")
                  }
                />
                <PlaceholderRow
                  label="Capacity / turbines"
                  value={
                    [
                      typeof selectedProject.capacity_mw === "number"
                        ? `${Number(
                            selectedProject.capacity_mw
                          ).toLocaleString()} MW`
                        : null,
                      selectedProject.turbine_count
                        ? `${selectedProject.turbine_count} turbines`
                        : null,
                    ]
                      .filter(Boolean)
                      .join(" · ") || "TBD"
                  }
                />
                <PlaceholderRow
                  label="Vintage"
                  value={
                    selectedProject.year
                      ? `Online ${selectedProject.year}`
                      : "Year TBD"
                  }
                />
                <PlaceholderRow
                  label="Lat / Lon"
                  value={
                    typeof selectedProject.lat === "number" &&
                    typeof selectedProject.lon === "number"
                      ? `${selectedProject.lat.toFixed(
                          4
                        )}, ${selectedProject.lon.toFixed(4)}`
                      : "Coordinates TBD"
                  }
                />
              </>
            ) : (
              <>
                <PlaceholderRow
                  label="Project name"
                  value="(select a wind farm on the left or click a marker)"
                />
                <PlaceholderRow
                  label="Owner / operator"
                  value="We’ll show the owner/operator if available in future releases."
                />
                <PlaceholderRow
                  label="Capacity / turbines"
                  value="Example: 200 MW · 100 turbines"
                />
                <PlaceholderRow
                  label="Vintage"
                  value="Example: Online 2014"
                />
                <PlaceholderRow
                  label="Lat / Lon"
                  value="Used for radius-based peer sets and map view."
                />
              </>
            )}

            <SectionHeader label="Assessment data (from your world)" />
            <PlaceholderRow
              label="Assessed value"
              value="Once we add saving, you’ll be able to link CAD values here per farm/year."
            />
            <PlaceholderRow
              label="AV per MW"
              value="Calculated as assessed value ÷ project capacity."
            />
            <PlaceholderRow
              label="Peer median AV/MW"
              value="Computed from similar wind farms (county, state, or radius)."
            />
            <PlaceholderRow
              label="Over-assessment signal"
              value="We’ll highlight where this project sits vs peers and estimate potential value reduction."
            />

            <SectionHeader label="Next steps" />
            <ul className="list-disc pl-5 space-y-1 text-[11px] text-slate-400">
              <li>
                Use the filters + map + list to find a cluster of similar wind
                farms in a given region.
              </li>
              <li>
                Select at least two and click{" "}
                <span className="font-semibold">Start Comp Set</span> to open
                the comp scratch pad.
              </li>
              <li>
                Once we’re happy with the workflow, we’ll wire this into
                persistent tables and reporting.
              </li>
            </ul>
          </>
        )}
      </div>
    </div>
  );
}

function CompSetTable({
  rows,
  subjectId,
  onSubjectIdChange,
  compValues,
  onUpdateAssessedValue,
}) {
  if (!rows.length) {
    return (
      <div className="text-[11px] text-slate-400">
        Select at least two projects on the left and click Start Comp Set.
      </div>
    );
  }

  const fmtNum = (n, decimals = 0) =>
    typeof n === "number" && !Number.isNaN(n)
      ? n.toLocaleString(undefined, { maximumFractionDigits: decimals })
      : "—";

  return (
    <div className="rounded-xl border border-slate-800 bg-slate-950/60 overflow-auto max-h-[280px]">
      <table className="min-w-full text-[11px]">
        <thead className="bg-slate-900/80 sticky top-0 z-10">
          <tr>
            <th className="px-3 py-2 text-left font-semibold text-slate-300">
              Subject
            </th>
            <th className="px-3 py-2 text-left font-semibold text-slate-300">
              Project
            </th>
            <th className="px-3 py-2 text-left font-semibold text-slate-300">
              Region
            </th>
            <th className="px-3 py-2 text-right font-semibold text-slate-300">
              MW
            </th>
            <th className="px-3 py-2 text-right font-semibold text-slate-300">
              Assessed value
            </th>
            <th className="px-3 py-2 text-right font-semibold text-slate-300">
              AV / MW
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const form = compValues[r.projectId] || {};
            const isSubject = subjectId === r.projectId;
            return (
              <tr
                key={r.projectId}
                className={
                  isSubject
                    ? "bg-sky-900/20 border-t border-slate-800"
                    : "border-t border-slate-800"
                }
              >
                <td className="px-3 py-2 align-top">
                  <input
                    type="radio"
                    name="wind-subject"
                    checked={isSubject}
                    onChange={() => onSubjectIdChange(r.projectId)}
                  />
                </td>
                <td className="px-3 py-2 align-top">
                  <div className="font-semibold text-slate-100 line-clamp-2">
                    {r.name}
                  </div>
                  <div className="text-[10px] text-slate-400">
                    {r.year ? `Online ${r.year}` : "Year TBD"}
                  </div>
                </td>
                <td className="px-3 py-2 align-top">
                  <div>
                    {[
                      r.state || "State",
                      r.counties && r.counties.length
                        ? `${r.counties.join(", ")}`
                        : null,
                    ]
                      .filter(Boolean)
                      .join(" · ")}
                  </div>
                </td>
                <td className="px-3 py-2 align-top text-right">
                  {fmtNum(r.capacity)}
                </td>
                <td className="px-3 py-2 align-top text-right">
                  <input
                    type="number"
                    className="w-full rounded bg-slate-900 border border-slate-600 px-2 py-1 text-right"
                    placeholder="0"
                    value={form.assessed ?? ""}
                    onChange={(e) =>
                      onUpdateAssessedValue(r.projectId, e.target.value)
                    }
                  />
                </td>
                <td className="px-3 py-2 align-top text-right">
                  {r.avPerMw
                    ? `$${fmtNum(r.avPerMw, 0)}`
                    : r.assessed && r.capacity
                    ? "Calc error"
                    : "—"}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function CompStatsPanel({ stats }) {
  if (!stats) {
    return (
      <div className="text-[11px] text-slate-400">
        Choose a subject and enter assessed values (including the subject). Once
        peers have AV/MW, we’ll show how the subject compares to the median.
      </div>
    );
  }

  const fmtMoney = (n) =>
    typeof n === "number" && !Number.isNaN(n)
      ? `$${n.toLocaleString(undefined, { maximumFractionDigits: 0 })}`
      : "—";

  const fmtPct = (d) =>
    typeof d === "number" && !Number.isNaN(d)
      ? (d * 100).toLocaleString(undefined, {
          maximumFractionDigits: 1,
        }) + "%"
      : "—";

  const direction =
    stats.delta > 0 ? "above" : stats.delta < 0 ? "below" : "at";
  const emphasis =
    stats.delta > 0
      ? "text-rose-300"
      : stats.delta < 0
      ? "text-emerald-300"
      : "text-slate-200";

  return (
    <div className="rounded-xl border border-slate-800 bg-slate-950/60 px-3 py-3">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
        <div className="text-[11px] text-slate-300">
          Median peer AV/MW:{" "}
          <span className="font-semibold">
            {fmtMoney(stats.medianAvPerMw)}
          </span>
        </div>
        <div className="text-[11px] text-slate-300">
          Subject AV/MW:{" "}
          <span className="font-semibold">
            {fmtMoney(stats.subjectAvPerMw)}
          </span>
        </div>
      </div>
      <div className="text-[11px] text-slate-300">
        Subject is{" "}
        <span className={["font-semibold", emphasis].join(" ")}>
          {fmtPct(stats.overPct)} {direction} the peer median
        </span>{" "}
        ({fmtMoney(stats.delta)} per MW), based on{" "}
        <span className="font-semibold">{stats.peerCount}</span> peers with AV/MW
        data.
      </div>
    </div>
  );
}
